import { Account } from "@ca/shared";
const origin: string = import.meta.env.VITE_API_ORIGIN ?? "http://localhost:53000";
let csrf = "";
let currentAccount: string | null = null;
let refreshPromise: Promise<void> | null = null;
export class HttpError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  constructor(status: number, code: string, message: string, details: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}
export const loginUrl = `${origin}/v1/auth/google`;
export function setIdentity(account: Account | null) {
  csrf = account?.csrfToken ?? "";
  currentAccount = account?.id ?? null;
}
async function parseResponse(response: Response): Promise<unknown> {
  const body: unknown = await response.json();
  if (!response.ok) {
    const value = body as { error?: { code: string; message: string; details: unknown } };
    if (
      value.error?.code === "feature_locked" &&
      value.error.details &&
      typeof value.error.details === "object" &&
      "upsell" in value.error.details
    )
      window.dispatchEvent(new CustomEvent("ca-upsell", { detail: value.error.details.upsell }));
    throw new HttpError(
      response.status,
      value.error?.code ?? "request_failed",
      value.error?.message ?? "Không kết nối được máy chủ.",
      value.error?.details ?? null,
    );
  }
  return body;
}
async function refresh() {
  if (!refreshPromise) {
    const action = async () => {
      // Tab khác có thể đã xoay cookie trong lúc tab này chờ khoá.
      let me = await fetch(`${origin}/v1/me`, { credentials: "include" });
      if (me.status === 401) {
        await parseResponse(
          await fetch(`${origin}/v1/auth/refresh`, { method: "POST", credentials: "include" }),
        );
        me = await fetch(`${origin}/v1/me`, { credentials: "include" });
      }
      const account = Account.parse(await parseResponse(me));
      if (currentAccount && account.id !== currentAccount)
        throw new HttpError(401, "account_changed", "Tài khoản đã thay đổi. Tải lại trang.", null);
      setIdentity(account);
    };
    refreshPromise = (
      typeof navigator.locks !== "undefined"
        ? navigator.locks.request("ca-auth-refresh", action)
        : action()
    ).finally(() => {
      refreshPromise = null;
    });
  }
  await refreshPromise;
}
export async function api(
  path: string,
  options: { method?: string; body?: unknown; key?: string; accountId?: string } = {},
): Promise<unknown> {
  const send = () =>
    fetch(`${origin}${path}`, {
      method: options.method ?? "GET",
      credentials: "include",
      headers: {
        ...(options.body === undefined ? {} : { "Content-Type": "application/json" }),
        "X-CSRF-Token": csrf,
        ...(options.key ? { "Idempotency-Key": options.key } : {}),
        ...(options.accountId ? { "X-Account-Id": options.accountId } : {}),
      },
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    });
  let response = await send();
  if (response.status === 401 && path !== "/v1/auth/logout") {
    await refresh();
    response = await send();
  }
  return parseResponse(response);
}
export async function loadMe() {
  const me = Account.parse(await api("/v1/me"));
  setIdentity(me);
  return me;
}
export async function logout() {
  await api("/v1/auth/logout", { method: "POST" });
  setIdentity(null);
}
